import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Badge,
  FormGroup,
  Input,
  Form,
  CardFooter,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Table,
} from "reactstrap";

import classnames from "classnames";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useGetPurchaseRequestByIdQuery,
  useCreateSuppliersAutoMapMutation,
  useUpdatePurchaseRequestMutation,
  useGetPurchaseOrderByRequestIdQuery,
  useCreatePurchaseAttachmentMutation,
  useDeletePurchaseAttachmentByIdMutation,
  useBulkMarkRfqMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import PurchaseOrderProduct from "./PurchaseOrderProduct";
import PreviewSection from "./PreviewSection";
import SupplierSelectionModal from "./SupplierSelectionModal";
import SupplierComparisonModal from "./SupplierComparisonModal";
import SupplierEmailModal from "./SupplierEmailModal";
import PurchaseOrderList from "./PurchaseOrdersList";
import Loader from "Components/Common/Loader";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";
import { IsValidPermissions } from "Routes/AuthProtected";

// Define types for grouped items
interface GroupedItems {
  [key: string]: any[];
}

interface Attachment {
  id: number;
  file_url: string;
  purchase_request_number: string;
  created_at: string;
  modified_at: string;
  active: boolean;
  name: string;
  description: string;
  file: string;
  created_by: number;
  modified_by: number;
  purchase_request: number;
  file_name?: string;
  file_size?: number;
}

const PurchaseOrderDetail = () => {
  const { purchaseId } = useParams();

  const {
    data: purchaseOrder,
    isLoading,
    error,
    refetch: refetchPurchaseProducts,
  } = useGetPurchaseRequestByIdQuery(purchaseId);

  const { data: purchaseOrdersData, refetch: purchaseOrdersDataRefetch } =
    useGetPurchaseOrderByRequestIdQuery(purchaseId);

  const [createPurchaseAttachment, { isLoading: isCreatingAttachment }] =
    useCreatePurchaseAttachmentMutation();
  const [deletePurchaseAttachmentById, { isLoading: isDeletingAttachment }] =
    useDeletePurchaseAttachmentByIdMutation();

  const [activeTab, setActiveTab] = useState("1");
  const [supplierSelectionModal, setSupplierSelectionModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState("No Action Required");
  const [isApplyingMapping, setIsApplyingMapping] = useState(false);
  const [showPreviewSection, setShowPreviewSection] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [rfqItems, setRfqItems] = useState<any[]>([]);

  // Attachment modal states
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [newAttachment, setNewAttachment] = useState({
    name: "",
    description: "",
    file: null as File | null,
  });

  // RFQ Selection modal state
  const [rfqSelectionModal, setRfqSelectionModal] = useState(false);
  
  // Supplier Comparison modal state
  const [supplierComparisonModal, setSupplierComparisonModal] = useState(false);
  
  // Supplier Email modal state
  const [supplierEmailModal, setSupplierEmailModal] = useState(false);
  const [selectedSupplierIdsForEmail, setSelectedSupplierIdsForEmail] = useState<number[]>([]);

  // Reject modal state (purchase request rejection)
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Auto mapping mutation
  const [createSuppliersAutoMap] = useCreateSuppliersAutoMapMutation();

  // Update purchase request mutation
  const [updatePurchaseRequest, { isLoading: isUpdatingTitle }] =
    useUpdatePurchaseRequestMutation();
  const [updatePurchaseRequestStatus, { isLoading: isUpdatingStatus }] =
    useUpdatePurchaseRequestMutation();

  // Bulk RFQ selection state for Purchase Request Products table
  const [selectedProductItemIds, setSelectedProductItemIds] = useState<number[]>([]);
  const [bulkMarkRfq, { isLoading: isBulkMarkingRfq }] = useBulkMarkRfqMutation();

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleMappingApplied = () => {
    // Refetch data to get updated suppliers
    refetchPurchaseProducts();
  };

  const toggleSupplierSelectionModal = () => {
    setSupplierSelectionModal(!supplierSelectionModal);
  };

  // RFQ Selection modal functions
  const toggleRfqSelectionModal = () => {
    setRfqSelectionModal(!rfqSelectionModal);
  };

  // Supplier Comparison modal functions
  const toggleSupplierComparisonModal = () => {
    setSupplierComparisonModal(!supplierComparisonModal);
  };

  // Supplier Email modal functions
  const toggleSupplierEmailModal = () => {
    setSupplierEmailModal(!supplierEmailModal);
  };

  const handleOpenSupplierEmailModal = () => {
    // Open supplier selection first, then email modal
    // For now, we'll open email modal directly - suppliers can be selected inside
    toggleSupplierEmailModal();
  };

  // Update the getRfqItems function to use state
  // Initialize RFQ items when purchaseOrder changes
  useEffect(() => {
    if (purchaseOrder?.items) {
      const rfqItemsFromAPI = purchaseOrder.items.filter(
        (item: any) => item.is_rfq === true
      );

      // Only update if items changed
      if (rfqItems.length === 0 || rfqItems.length !== rfqItemsFromAPI.length) {
        const itemsWithSelection = rfqItemsFromAPI.map((item: any) => ({
          ...item,
          selected: false,
        }));
        setRfqItems(itemsWithSelection);
      }
    }
  }, [purchaseOrder?.items]); // Only run when purchaseOrder.items changes

  // Simplified getRfqItems - now just returns the state
  const getRfqItems = () => {
    return rfqItems;
  };

  // Handle select all checkbox
  const handleSelectAll = (isChecked: boolean) => {
    const updatedItems = rfqItems.map((item) => ({
      ...item,
      selected: isChecked,
    }));
    setRfqItems(updatedItems);
  };

  // Handle individual checkbox selection
  const handleIndividualSelect = (itemId: number, isChecked: boolean) => {
    const updatedItems = rfqItems.map((item) =>
      item.id === itemId ? { ...item, selected: isChecked } : item
    );
    setRfqItems(updatedItems);
  };

  // Check if all items are selected
  const isAllSelected =
    rfqItems.length > 0 && rfqItems.every((item) => item.selected);

  // Get selected items count
  const selectedItemsCount = rfqItems.filter((item) => item.selected).length;

  const copySelectedRfqItemsToClipboard = () => {
    const selectedItems = rfqItems.filter((item: any) => item.selected);
    if (selectedItems.length === 0) return;

    // Calculate maximum lengths for each column
    const maxProductNameLength = Math.max(
      25,
      ...selectedItems.map((item: any) => item.product_name.length)
    );
    const maxQuantityLength = Math.max(
      8,
      ...selectedItems.map((item: any) =>
        `${item.quantity} ${item.measurement_name || ""}`.length
      )
    );
    const maxDeliveryDateLength = 16; // Fixed as dates have consistent format
    const maxDeliveryNoteLength = Math.max(
      12,
      ...selectedItems.map((item: any) =>
        (item.delivery_note || "No notes").length
      )
    );
    const maxTotalAmountLength = 10; // Fixed as currency format is consistent

    let rfqText = `RFQ ITEMS LIST - QUOTATION REQUEST
=========================================
Purchase Request: ${purchaseOrder?.purchase_request_number || "N/A"}
Date: ${new Date().toLocaleDateString()}
Total Selected Items: ${selectedItems.length}

+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+
| No. | ${"Product Name".padEnd(maxProductNameLength)} | ${"Quantity".padEnd(maxQuantityLength)} | ${"Expected Delivery".padEnd(maxDeliveryDateLength)} | ${"Delivery Note".padEnd(maxDeliveryNoteLength)} | ${"Total Amount".padEnd(maxTotalAmountLength)} |
+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+`;

    selectedItems.forEach((item: any, index: number) => {
      const productName = item.product_name.padEnd(maxProductNameLength);
      const quantity = `${item.quantity} ${item.measurement_name || ""}`.padEnd(maxQuantityLength);
      const deliveryDate = item.expected_delivery_date
        ? new Date(item.expected_delivery_date).toLocaleDateString().padEnd(maxDeliveryDateLength)
        : "Not specified".padEnd(maxDeliveryDateLength);
      const deliveryNote = (item.delivery_note || "No notes").padEnd(maxDeliveryNoteLength);
      const totalAmount = `$${item.total_item_cost
          ? parseFloat(item.total_item_cost).toFixed(2)
          : "0.00"
        }`.padEnd(maxTotalAmountLength);

      rfqText += `\n| ${(index + 1)
        .toString()
        .padEnd(3)} | ${productName} | ${quantity} | ${deliveryDate} | ${deliveryNote} | ${totalAmount} |`;
    });

    rfqText += `\n+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+

IMPORTANT: PLEASE QUOTE THE AMOUNT FOR THE PRODUCTS
===================================================
We request you to provide your competitive quotation for the above products.

Please include in your quotation:
• Unit price for each product
• Total amount for the requested quantities
• Delivery timeline and lead time
• Any applicable taxes and additional charges
• Payment terms and conditions
• Quotation validity period
• Your company details and contact information

We look forward to your prompt response and competitive pricing.

Thank you for your business!`;

    navigator.clipboard
      .writeText(rfqText)
      .then(() => {
        alert(
          `${selectedItems.length} selected RFQ items copied to clipboard!`
        );
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        alert("Failed to copy to clipboard. Please try again.");
      });
  };

  // Update the copyAllRfqItemsToClipboard function as well
  const copyAllRfqItemsToClipboard = () => {
    const rfqItems = getRfqItems();
    if (rfqItems.length === 0) return;

    // Calculate maximum lengths for each column
    const maxProductNameLength = Math.max(
      25,
      ...rfqItems.map((item: any) => item.product_name.length)
    );
    const maxQuantityLength = Math.max(
      8,
      ...rfqItems.map((item: any) =>
        `${item.quantity} ${item.measurement_name || ""}`.length
      )
    );
    const maxDeliveryDateLength = 16;
    const maxDeliveryNoteLength = Math.max(
      12,
      ...rfqItems.map((item: any) =>
        (item.delivery_note || "No notes").length
      )
    );
    const maxTotalAmountLength = 10;

    let rfqText = `RFQ ITEMS LIST - QUOTATION REQUEST
=========================================
Purchase Request: ${purchaseOrder?.purchase_request_number || "N/A"}
Date: ${new Date().toLocaleDateString()}
Total RFQ Items: ${rfqItems.length}

+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+
| No. | ${"Product Name".padEnd(maxProductNameLength)} | ${"Quantity".padEnd(maxQuantityLength)} | ${"Expected Delivery".padEnd(maxDeliveryDateLength)} | ${"Delivery Note".padEnd(maxDeliveryNoteLength)} | ${"Total Amount".padEnd(maxTotalAmountLength)} |
+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+`;

    rfqItems.forEach((item: any, index: number) => {
      const productName = item.product_name.padEnd(maxProductNameLength);
      const quantity = `${item.quantity} ${item.measurement_name || ""}`.padEnd(maxQuantityLength);
      const deliveryDate = item.expected_delivery_date
        ? new Date(item.expected_delivery_date).toLocaleDateString().padEnd(maxDeliveryDateLength)
        : "Not specified".padEnd(maxDeliveryDateLength);
      const deliveryNote = (item.delivery_note || "No notes").padEnd(maxDeliveryNoteLength);
      const totalAmount = `$${item.total_item_cost
          ? parseFloat(item.total_item_cost).toFixed(2)
          : "0.00"
        }`.padEnd(maxTotalAmountLength);

      rfqText += `\n| ${(index + 1)
        .toString()
        .padEnd(3)} | ${productName} | ${quantity} | ${deliveryDate} | ${deliveryNote} | ${totalAmount} |`;
    });

    rfqText += `\n+-----+${'-'.repeat(maxProductNameLength + 2)}+${'-'.repeat(maxQuantityLength + 2)}+${'-'.repeat(maxDeliveryDateLength + 2)}+${'-'.repeat(maxDeliveryNoteLength + 2)}+${'-'.repeat(maxTotalAmountLength + 2)}+

MESSAGE TO SUPPLIERS:
====================
PLEASE QUOTE THE AMOUNT FOR THE ABOVE PRODUCTS

We are seeking competitive quotations for the products listed above. Please provide:

PRICING INFORMATION:
• Unit price for each product
• Total amount for the requested quantities
• Any volume discounts available

DELIVERY & TERMS:
• Delivery schedule and lead time
• Delivery terms and conditions
• Any additional charges (shipping, handling, etc.)

QUOTATION DETAILS:
• Quotation validity period
• Payment terms
• Your company registration details
• Contact information for follow-up

We appreciate your prompt response and look forward to receiving your competitive quotation.

Thank you for your business!`;

    navigator.clipboard
      .writeText(rfqText)
      .then(() => {
        alert(`All ${rfqItems.length} RFQ items copied to clipboard!`);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        alert("Failed to copy to clipboard. Please try again.");
      });
  };

  // Also update the single item copy function
  const copySingleRfqItemToClipboard = (item: any) => {
    const rfqText = `RFQ ITEM DETAILS - QUOTATION REQUEST
================================
PRODUCT INFORMATION:
• Product Name: ${item.product_name}
• Product ID: ${item.product}
• Quantity: ${item.quantity} ${item.measurement_name}
• Expected Delivery: ${item.expected_delivery_date
        ? new Date(item.expected_delivery_date).toLocaleDateString()
        : "Not specified"
      }
• Delivery Note: ${item.delivery_note || "No notes"}
• Current Total Amount: $${item.total_item_cost
        ? parseFloat(item.total_item_cost).toFixed(2)
        : "0.00"
      }

IMPORTANT: PLEASE QUOTE YOUR AMOUNT
===================================
We request you to provide your competitive quotation for this product.

Please include in your quotation:
• Your unit price
• Total amount for ${item.quantity} ${item.measurement_name}
• Delivery timeline
• Any additional terms and conditions
• Quotation validity period

We look forward to your competitive pricing.`;

    navigator.clipboard
      .writeText(rfqText)
      .then(() => {
        alert("RFQ details copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        alert("Failed to copy to clipboard. Please try again.");
      });
  };

  // Attachment modal functions
  const toggleAttachmentModal = () => {
    setAttachmentModal(!attachmentModal);
    if (attachmentModal) {
      // Reset form when closing modal
      setNewAttachment({
        name: "",
        description: "",
        file: null,
      });
    }
  };

  // Handle attachment input changes
  const handleAttachmentInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewAttachment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewAttachment((prev) => ({
      ...prev,
      file,
      name: file?.name || prev.name, // Auto-fill name with filename if empty
    }));
  };

  // Create new attachment
  const handleCreateAttachment = async () => {
    if (!purchaseId || !newAttachment.file) {
      alert("Please select a file to upload");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newAttachment.name || newAttachment.file.name);
      formData.append("description", newAttachment.description);
      formData.append("file", newAttachment.file);
      formData.append("purchase_request", purchaseId);

      await createPurchaseAttachment(formData).unwrap();

      // Refresh data and close modal
      refetchPurchaseProducts();
      toggleAttachmentModal();

      alert("Attachment uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      alert("Failed to upload attachment. Please try again.");
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) {
      return;
    }

    try {
      ///@ts-ignore
      await deletePurchaseAttachmentById(attachmentId).unwrap();
      refetchPurchaseProducts();
      alert("Attachment deleted successfully!");
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      alert("Failed to delete attachment. Please try again.");
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get file name from URL/path
  const getFileName = (filePath: string) => {
    return filePath.split("/").pop() || "Unknown file";
  };

  // Title editing function
  const handleEditTitle = () => {
    setEditedTitle(purchaseOrder?.title || "");
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setEditedTitle("");
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    if (!purchaseId || !editedTitle.trim()) return;

    try {
      await updatePurchaseRequest({
        id: parseInt(purchaseId),
        productData: { title: editedTitle.trim() },
      }).unwrap();

      setIsEditingTitle(false);
      setEditedTitle("");
      refetchPurchaseProducts(); // Refetch to get updated data
    } catch (error) {
      console.error("Failed to update title:", error);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  // Add function to handle preview button click
  const handlePreviewClick = () => {
    setShowPreviewSection(true);
  };

  // Add function to handle close preview
  const handleClosePreview = () => {
    setShowPreviewSection(false);
  };

  const handleApplyMapping = async () => {
    if (!purchaseId || selectedOption === "No Action Required") return;

    setIsApplyingMapping(true);
    try {
      let method = "";

      if (selectedOption === "Select Suppliers by Low Cost") {
        method = "lowest_cost";
      } else if (selectedOption === "Select Suppliers by Previous Order") {
        method = "previous_order";
      }

      const requestData = {
        purchase_request_id: parseInt(purchaseId),
        method: method,
      };

      console.log("Applying supplier mapping:", requestData);

      const result = await createSuppliersAutoMap(requestData).unwrap();
      console.log("Supplier mapping result:", result);
      refetchPurchaseProducts();
    } catch (error) {
      console.error("Failed to apply supplier mapping:", error);
    } finally {
      setIsApplyingMapping(false);
    }
  };

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
    console.log("Selected option:", option);
  };

  const handleRejectConfirm = async () => {
    if (!purchaseId) return;
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      await updatePurchaseRequestStatus({
        id: parseInt(purchaseId),
        productData: {
          status: "rejected",
          rejection_reason: rejectReason.trim(),
        },
      }).unwrap();
      setRejectModal(false);
      setRejectReason("");
      refetchPurchaseProducts();
      alert("Purchase request has been rejected. User can now edit and resubmit.");
    } catch (err: any) {
      console.error("Failed to reject purchase request:", err);
      const errorMsg = err?.data?.error || err?.data?.message || err?.message || "Failed to reject. Please try again.";
      alert(errorMsg);
    }
  };

  const toggleRejectModal = () => {
    setRejectModal((prev) => !prev);
    if (!rejectModal) setRejectReason("");
  };

  // Define status badge colors
  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      draft: { color: "secondary", label: "Draft" },
      submitted: { color: "warning", label: "Submitted" },
      approved: { color: "success", label: "Approved" },
      rejected: { color: "danger", label: "Rejected" },
      resubmit: { color: "info", label: "Resubmit" },
      ordered: { color: "info", label: "Ordered" },
      delivered: { color: "primary", label: "Delivered" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Get mapping method display name
  const getMappingMethodDisplay = (method: string) => {
    const methodMap: { [key: string]: string } = {
      lowest_cost: "Lowest Cost",
      previous_order: "Previous Order",
    };
    return methodMap[method] || method;
  };

  // Check if purchase order has items
  const hasPurchaseOrderItems =
    purchaseOrder?.items && purchaseOrder.items.length > 0;

  // Items that can be bulk-marked as RFQ (no PO created and request not rejected)
  const selectableProductItems =
    purchaseOrder?.items?.filter((item: any) => !item.is_po_created) || [];
  const selectableProductItemIds = selectableProductItems.map((item: any) => item.id);

  const handleToggleProductSelection = (itemId: number) => {
    setSelectedProductItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSelectAllProducts = (isChecked: boolean) => {
    if (!isChecked) {
      setSelectedProductItemIds([]);
    } else {
      setSelectedProductItemIds(selectableProductItemIds);
    }
  };

  const isAllProductsSelected =
    selectableProductItemIds.length > 0 &&
    selectableProductItemIds.every((id: number) => selectedProductItemIds.includes(id));

  const selectedProductsCount = selectedProductItemIds.length;

  // Check if there are RFQ items
  const hasRfqItems = getRfqItems().length > 0;

  // Check if all items have suppliers
  const canSubmitOrder = () => {
    if (!purchaseOrder?.items || purchaseOrder.items.length === 0) return false;
    return purchaseOrder.items.every(
      (item: any) => item.supplier || item.is_rfq
    );
  };

  // Check if any items are not marked as po_created
  const hasItemsWithoutPO = purchaseOrder?.items?.some(
    (item: any) => !item.is_po_created
  ) || false;

  // Purchase request reject button (show if any items not marked as po_created)
  const canShowRejectButton =
    hasItemsWithoutPO &&
    purchaseId &&
    purchaseOrder?.status?.toLowerCase() !== "rejected" &&
    (IsValidPermissions(["EDIT_PURCHASE_REQUEST"]) || IsValidPermissions(["ADD_PURCHASE_REQUEST"]));

  // Disable supplier selection if status is rejected
  const isSupplierSelectionDisabled = purchaseOrder?.status?.toLowerCase() === "rejected";

  const handleBulkMarkAsRfq = async () => {
    if (selectedProductItemIds.length === 0) return;
    try {
      await bulkMarkRfq({ item_ids: selectedProductItemIds, is_rfq: true }).unwrap();
      setSelectedProductItemIds([]);
      refetchPurchaseProducts();
      alert("Selected items have been marked as RFQ.");
    } catch (error: any) {
      console.error("Failed to bulk mark RFQ items:", error);
      const message =
        error?.data?.detail ||
        error?.data?.error ||
        error?.error ||
        "Failed to bulk mark items as RFQ. Please try again.";
      alert(String(message));
    }
  };

  document.title = "Purchase Order Fulfilment";

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger">
            Error loading purchase order fulfilment.
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Purchase Order Fulfilment" pageTitle="Inventory" />

        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      {isEditingTitle ? (
                        <Form className="d-flex align-items-center gap-2">
                          <FormGroup className="mb-0">
                            <Input
                              type="text"
                              value={editedTitle}
                              onChange={handleTitleChange}
                              placeholder="Enter purchase order title"
                              disabled={isUpdatingTitle}
                              className="fw-bold fs-5"
                              autoFocus
                            />
                          </FormGroup>
                          <div className="d-flex gap-1">
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleSaveTitle}
                              disabled={isUpdatingTitle || !editedTitle.trim()}
                            >
                              {isUpdatingTitle ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <i className="ri-check-line" />
                              )}
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleCancelEditTitle}
                              disabled={isUpdatingTitle}
                            >
                              <i className="ri-close-line" />
                            </Button>
                          </div>
                        </Form>
                      ) : (
                        <>
                          <h5 className="card-title mb-0 me-2">
                            {purchaseOrder?.title || "Untitled Purchase Order"}
                          </h5>
                          {!purchaseOrder?.title && (
                            <Button
                              color="link"
                              size="sm"
                              onClick={handleEditTitle}
                              className="p-0 text-muted"
                              title="Edit title"
                            >
                              <i className="ri-edit-2-line fs-14" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="text-muted me-2">
                        Purchase Fullfilment #
                        {purchaseOrder?.purchase_order_number || "N/A"}
                      </span>
                      {purchaseOrder?.status && (
                        <span className="ms-2">
                          {getStatusBadge(purchaseOrder.status)}
                        </span>
                      )}
                      {purchaseOrder?.last_supplier_mapping_method && (
                        <Badge color="info" className="ms-2">
                          <i className="ri-filter-line align-middle me-1"></i>
                          {getMappingMethodDisplay(
                            purchaseOrder.last_supplier_mapping_method
                          )}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {canShowRejectButton && (
                    <div className="d-flex gap-2">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={toggleRejectModal}
                        disabled={isUpdatingStatus}
                      >
                        <i className="ri-close-line align-middle me-1"></i>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Purchase Request Number</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.purchase_request_number || "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Summary</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.summary || "No summary provided"}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Remarks</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.remarks || "No Remarks provided"}
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Project</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.project_name || "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Submitted By</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.submitted_by_name || "N/A"}
                      </p>
                    </div>
                    {purchaseOrder?.last_supplier_mapping_method && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Supplier Mapping Method
                        </label>
                        <p className="text-muted mb-0">
                          {getMappingMethodDisplay(
                            purchaseOrder.last_supplier_mapping_method
                          )}
                        </p>
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Horizontal Separator Line */}
                <hr className="my-4" />

                {/* Tabs Navigation */}
                <Nav tabs className="nav-tabs nav-bordered mb-4">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                    >
                      <i className="ri-shopping-bag-line align-middle me-1"></i>
                      Purchase Order
                    </NavLink>
                  </NavItem>
                  {/* Documents/Attachments Tab */}
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "5" })}
                      onClick={() => toggleTab("5")}
                    >
                      <i className="ri-attachment-line align-middle me-1"></i>
                      Documents
                      {purchaseOrder?.attachments?.length > 0 && (
                        <Badge color="primary" pill className="ms-1">
                          {purchaseOrder.attachments.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                </Nav>

                {/* Tab Content */}
                <TabContent activeTab={activeTab}>
                  {/* Tab 1: Purchase Order */}
                  <TabPane tabId="1">
                    <Row>
                      <Col lg={12}>
                        {/* Purchase Order Details Section */}
                        <Card className="mb-4 border-0 shadow-sm">
                          <CardHeader className="bg-light border-bottom">
                            <h5 className="card-title mb-0 text-primary">
                              <i className="ri-file-list-line align-middle me-2"></i>
                              Purchase Order Details
                            </h5>
                          </CardHeader>
                          <CardBody className="p-4">
                            <PurchaseOrderList
                              purchaseRequestId={purchaseId!}
                            />
                          </CardBody>
                        </Card>

                        {/* Section Separator - Only show when there are items and not in preview mode */}
                        {hasPurchaseOrderItems && !showPreviewSection && (
                          <div className="position-relative my-4">
                            <hr className="border-2 border-dashed" />
                            <div className="position-absolute top-50 start-50 translate-middle bg-white px-3">
                              <span className="text-muted small">
                                <i className="ri-arrow-down-line align-middle me-1"></i>
                                Products List
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Preview Section - Show only when preview button is clicked */}
                        {showPreviewSection && (
                          <>
                            <div className="position-relative my-4">
                              <hr className="border-2 border-primary border-dashed" />
                              <div className="position-absolute top-50 start-50 translate-middle bg-white px-3">
                                <span className="text-primary fw-semibold small">
                                  <i className="ri-eye-line align-middle me-1"></i>
                                  Order Preview
                                </span>
                              </div>
                            </div>
                            <PreviewSection
                              purchaseOrder={purchaseOrder}
                              onClosePreview={handleClosePreview}
                              onOrderCreated={refetchPurchaseProducts} // This will refetch data when PO is created
                              purchaseOrdersDataRefetch={
                                purchaseOrdersDataRefetch
                              }
                            />
                          </>
                        )}

                        {/* Purchase Order Products Section - Hide when preview is shown */}
                        {hasPurchaseOrderItems && !showPreviewSection && (
                          <Card className="border-0 shadow-sm">
                            <CardHeader className="bg-light border-bottom">
                              <div className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0 text-primary">
                                  <i className="ri-shopping-cart-line align-middle me-2"></i>
                                  Purchase Request Products
                                </h5>
                                <div className="d-flex align-items-center gap-2">
                                  {selectableProductItemIds.length > 0 && (
                                    <Badge color="primary">
                                      {selectedProductsCount} selected
                                    </Badge>
                                  )}
                                  {IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"]) && (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      className="rounded-pill"
                                      disabled={
                                        selectedProductsCount === 0 ||
                                        isSupplierSelectionDisabled ||
                                        isBulkMarkingRfq
                                      }
                                      onClick={handleBulkMarkAsRfq}
                                    >
                                      {isBulkMarkingRfq ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" />
                                          Updating...
                                        </>
                                      ) : (
                                        <>
                                          <i className="ri-mail-check-line align-middle me-1"></i>
                                          Mark selected as RFQ
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardBody className="p-0">
                              <div className="table-responsive">
                                <div className="table-body-container">
                                  <table className="table table-nowrap align-middle table-borderless mb-0">
                                    <thead
                                      className="table-light text-muted"
                                      style={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                      }}
                                    >
                                      <tr>
                                        <th
                                          scope="col"
                                          className="ps-4"
                                          style={{ width: "50px" }}
                                        >
                                          <Input
                                            type="checkbox"
                                            id="selectAllProductsRfq"
                                            checked={isAllProductsSelected}
                                            disabled={
                                              isSupplierSelectionDisabled ||
                                              selectableProductItemIds.length === 0
                                            }
                                            onChange={(e) =>
                                              handleSelectAllProducts(e.target.checked)
                                            }
                                          />
                                        </th>
                                        <th scope="col">
                                          Product Details
                                        </th>
                                        <th scope="col" className="text-center">
                                          Quantity
                                        </th>
                                        <th scope="col" className="text-center">
                                          Expected Delivery Date
                                        </th>
                                        <th scope="col" className="text-center">
                                          Delivery Note
                                        </th>
                                        <th scope="col" className="text-center">
                                          Supplier
                                        </th>
                                        <th
                                          scope="col"
                                          className="text-center pe-4"
                                        >
                                          Total Cost
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {purchaseOrder?.items?.map(
                                        (item: any, index: number) => (
                                          <React.Fragment key={item.id}>
                                            <PurchaseOrderProduct
                                              item={item}
                                              refetchPurchaseProducts={
                                                refetchPurchaseProducts
                                              }
                                              purchaseOrderStatus={purchaseOrder?.status}
                                              targetProjectId={purchaseOrder?.project_id}
                                              isSelectable={
                                                !item.is_po_created && !isSupplierSelectionDisabled
                                              }
                                              isSelected={selectedProductItemIds.includes(
                                                item.id
                                              )}
                                              onToggleSelect={() =>
                                                handleToggleProductSelection(item.id)
                                              }
                                            />
                                            {index <
                                              purchaseOrder.items.length -
                                              1 && (
                                                <tr>
                                                  <td colSpan={6} className="p-0">
                                                    <hr className="my-1 opacity-25" />
                                                  </td>
                                                </tr>
                                              )}
                                          </React.Fragment>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </CardBody>
                            {/* Supplier Filter Button in Card Footer */}
                            <CardFooter className="bg-light border-top">
                              <div className="d-flex justify-content-between align-items-center">
                                {/* <small className="text-muted">
                                  Use supplier filter to automatically assign
                                  suppliers to items
                                </small> */}
                                <div className="d-flex gap-2">
                                  {/* {!purchaseOrder?.last_supplier_mapping_method && (
                                    <Button
                                      color="primary"
                                      onClick={toggleSupplierSelectionModal}
                                      className="rounded-pill"
                                      disabled={isSupplierSelectionDisabled}
                                      title={isSupplierSelectionDisabled ? "Cannot select suppliers - Purchase request is rejected" : "Use supplier filter to automatically assign suppliers"}
                                    >
                                      <i className="ri-filter-line align-middle me-1"></i>
                                      Supplier Filter
                                    </Button>
                                  )} */}
                                  {/* RFQ Selection Button - Hide if rejected */}
                                  {hasRfqItems && !isSupplierSelectionDisabled && (
                                    <Button
                                      color="warning"
                                      onClick={toggleRfqSelectionModal}
                                      className="rounded-pill"
                                    >
                                      <i className="ri-file-list-line align-middle me-1"></i>
                                      RFQ Selection ({getRfqItems().length})
                                    </Button>
                                  )}
                                  {/* Preview Button */}
                                  <Button
                                    color="success"
                                    onClick={handlePreviewClick}
                                    className="rounded-pill"
                                    disabled={!hasPurchaseOrderItems}
                                  >
                                    <i className="ri-eye-line align-middle me-1"></i>
                                    Preview
                                  </Button>
                                </div>
                              </div>
                            </CardFooter>
                          </Card>
                        )}
                      </Col>
                    </Row>
                  </TabPane>

                  {/* Tab 5: Documents/Attachments */}
                  <TabPane tabId="5">
                    <Row>
                      <Col lg={12}>
                        <Card className="border-0 shadow-sm">
                          <CardHeader className="bg-light border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="card-title mb-0 text-primary">
                                <i className="ri-attachment-line align-middle me-2"></i>
                                Documents & Attachments
                              </h5>
                              <Button
                                color="primary"
                                onClick={toggleAttachmentModal}
                                className="rounded-pill"
                              >
                                <i className="ri-add-line align-middle me-1"></i>
                                Add Attachment
                              </Button>
                            </div>
                          </CardHeader>
                          <CardBody className="p-4">
                            {purchaseOrder?.attachments?.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th scope="col">File Name</th>
                                      <th scope="col">Description</th>
                                      <th scope="col">Uploaded Date</th>
                                      <th scope="col">File Size</th>
                                      <th scope="col" className="text-center">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {purchaseOrder.attachments.map(
                                      (attachment: Attachment) => (
                                        <tr key={attachment.id}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <i className="ri-file-text-line text-primary fs-5 me-2"></i>
                                              <div>
                                                <div className="fw-semibold">
                                                  <a
                                                    href={attachment.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none"
                                                  >
                                                    {attachment.name ||
                                                      getFileName(
                                                        attachment.file
                                                      )}
                                                  </a>
                                                </div>
                                                <small className="text-muted">
                                                  {getFileName(attachment.file)}
                                                </small>
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            <span className="text-muted">
                                              {attachment.description ||
                                                "No description"}
                                            </span>
                                          </td>
                                          <td>
                                            <small className="text-muted">
                                              {new Date(
                                                attachment.created_at
                                              ).toLocaleDateString()}
                                            </small>
                                          </td>
                                          <td>
                                            <Badge
                                              color="light"
                                              className="text-muted"
                                            >
                                              {attachment.file_size
                                                ? formatFileSize(
                                                  attachment.file_size
                                                )
                                                : "N/A"}
                                            </Badge>
                                          </td>
                                          <td className="text-center">
                                            <div className="hstack gap-2 justify-content-center">
                                              <Button
                                                color="primary"
                                                size="sm"
                                                className="btn-icon"
                                                tag="a"
                                                href={attachment.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View"
                                              >
                                                <i className="ri-eye-line"></i>
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                className="btn-icon"
                                                tag="a"
                                                href={attachment.file_url}
                                                target="_blank"
                                                download
                                                title="Download"
                                              >
                                                <i className="ri-download-line"></i>
                                              </Button>
                                              <Button
                                                color="danger"
                                                size="sm"
                                                className="btn-icon"
                                                onClick={() =>
                                                  handleDeleteAttachment(
                                                    attachment.id
                                                  )
                                                }
                                                disabled={isDeletingAttachment}
                                                title="Delete"
                                              >
                                                {isDeletingAttachment ? (
                                                  <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                  <i className="ri-delete-bin-line"></i>
                                                )}
                                              </Button>
                                            </div>
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-attachment-line display-4 text-muted opacity-50"></i>
                                <h5 className="mt-3 text-muted">
                                  No Attachments
                                </h5>
                                <p className="text-muted mb-4">
                                  No documents have been attached to this
                                  purchase request yet.
                                </p>
                                <Button
                                  color="primary"
                                  onClick={toggleAttachmentModal}
                                  className="rounded-pill"
                                >
                                  <i className="ri-add-line align-middle me-1"></i>
                                  Add Your First Attachment
                                </Button>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>

            {/* Supplier Selection Modal */}
            {!isSupplierSelectionDisabled && (
              <SupplierSelectionModal
                isOpen={supplierSelectionModal}
                toggle={toggleSupplierSelectionModal}
                purchaseOrder={purchaseOrder}
                onMappingApplied={handleMappingApplied}
              />
            )}

            {/* RFQ Selection Modal - Hide if rejected */}
            {!isSupplierSelectionDisabled && (
              <Modal
                isOpen={rfqSelectionModal}
                toggle={toggleRfqSelectionModal}
                size="xl"
              >
              <ModalHeader
                toggle={toggleRfqSelectionModal}
                className="bg-light py-3"
              >
                <div className="d-flex align-items-center">
                  <i className="ri-file-list-line text-warning me-2 fs-5"></i>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">
                      RFQ Items Selection
                    </h5>
                    <small className="text-muted">
                      Select items to request quotations from suppliers
                    </small>
                  </div>
                </div>
                <Badge color="warning" className="ms-2 fs-6">
                  {getRfqItems().length} Items
                </Badge>
              </ModalHeader>

              <ModalBody className="p-0">
                {hasRfqItems ? (
                  <div>
                    {/* Selection Header */}
                    <div className="p-3 border-bottom bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <FormGroup check className="mb-0 me-3">
                            <Input
                              type="checkbox"
                              id="selectAllRfq"
                              checked={isAllSelected}
                              onChange={(e) =>
                                handleSelectAll(e.target.checked)
                              }
                            />
                            <Label
                              for="selectAllRfq"
                              check
                              className="fw-semibold text-dark mb-0"
                            >
                              Select All
                            </Label>
                          </FormGroup>
                          <Badge color="primary" className="fs-6">
                            {selectedItemsCount} selected
                          </Badge>
                        </div>
                        <Button
                          color="warning"
                          onClick={copySelectedRfqItemsToClipboard}
                          disabled={selectedItemsCount === 0}
                          className="rounded-pill"
                        >
                          <i className="ri-clipboard-line align-middle me-1"></i>
                          Copy Selected
                        </Button>
                      </div>
                    </div>

                    {/* RFQ Items Table */}
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "400px" }}
                    >
                      <Table hover className="align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th
                              scope="col"
                              style={{ width: "50px" }}
                              className="ps-4"
                            >
                              Select
                            </th>
                            <th scope="col" style={{ width: "50px" }}>
                              #
                            </th>
                            <th scope="col">Product Details</th>
                            <th scope="col" className="text-center">
                              Quantity
                            </th>
                            <th scope="col" className="text-center">
                              Expected Delivery
                            </th>
                            <th scope="col" className="text-center">
                              Delivery Note
                            </th>
                            <th scope="col" className="text-center">
                              Total Amount
                            </th>
                            <th scope="col" className="text-center pe-4">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rfqItems.map((item: any, index: number) => (
                            <tr
                              key={item.id}
                              className={item.selected ? "table-warning" : ""}
                            >
                              <td className="ps-4">
                                <FormGroup check className="mb-0">
                                  <Input
                                    type="checkbox"
                                    id={`rfq-item-${item.id}`}
                                    checked={item.selected || false}
                                    onChange={(e) =>
                                      handleIndividualSelect(
                                        item.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </FormGroup>
                              </td>
                              <td>
                                <span className="fw-semibold text-muted">
                                  {index + 1}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0 me-3">
                                    <ProductThumbnail
                                          attachment_urls={item?.attachment_urls}
                                          alt={item?.product_name || ''}
                                          size="lg"
                                          className="me-2"
                                        />
                                    {/* {item.thumbnail_url ? (
                                      <img
                                        src={item.thumbnail_url}
                                        alt={item.product_name}
                                        className="img-fluid rounded border"
                                        style={{
                                          width: "45px",
                                          height: "45px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className="bg-light rounded border d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "45px",
                                          height: "45px",
                                        }}
                                      >
                                        <i className="ri-box-3-line text-muted"></i>
                                      </div>
                                    )} */}
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="mb-1 fw-semibold">
                                      {item.product_name}
                                    </h6>
                                    <small className="text-muted d-block">
                                      Category: {item.category_name}
                                    </small>
                                    {item.tag_names &&
                                      item.tag_names.length > 0 && (
                                        <small className="text-muted">
                                          Tags: {item.tag_names.join(", ")}
                                        </small>
                                      )}
                                  </div>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="fw-bold text-dark">
                                  {item.quantity}
                                </span>
                                {item.measurement_name && (
                                  <div className="text-muted fs-12">
                                    {item.measurement_name}
                                  </div>
                                )}
                              </td>
                              <td className="text-center">
                                {item.expected_delivery_date ? (
                                  <Badge color="info" className="fs-12">
                                    {new Date(
                                      item.expected_delivery_date
                                    ).toLocaleDateString()}
                                  </Badge>
                                ) : (
                                  <span className="text-muted fs-12">
                                    Not specified
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                {item.delivery_note ? (
                                  <span className="text-muted fs-12">
                                    {item.delivery_note}
                                  </span>
                                ) : (
                                  <span className="text-muted fs-12">
                                    No notes
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge color="success" className="fs-12">
                                  $
                                  {item.total_item_cost
                                    ? parseFloat(item.total_item_cost).toFixed(
                                      2
                                    )
                                    : "0.00"}
                                </Badge>
                              </td>
                              <td className="text-center pe-4">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() =>
                                    copySingleRfqItemToClipboard(item)
                                  }
                                  className="btn-icon rounded-circle"
                                  title="Copy RFQ details to clipboard"
                                >
                                  <i className="ri-clipboard-line"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* Summary Section */}
                    <div className="p-3 border-top bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="text-muted">
                            Total Items: <strong>{rfqItems.length}</strong> •
                            Selected:{" "}
                            <strong className="text-primary">
                              {selectedItemsCount}
                            </strong>{" "}
                            • Total Value:{" "}
                            <strong className="text-success">
                              $
                              {rfqItems
                                .reduce(
                                  (sum, item) =>
                                    sum +
                                    (parseFloat(item.total_item_cost) || 0),
                                  0
                                )
                                .toFixed(2)}
                            </strong>
                          </span>
                        </div>
                        {selectedItemsCount > 0 && (
                          <Badge color="success" className="fs-12">
                            <i className="ri-check-line me-1"></i>
                            Ready to copy {selectedItemsCount} items
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-file-list-line display-4 text-muted opacity-50 mb-3"></i>
                    <h5 className="text-muted mb-2">No RFQ Items</h5>
                    <p className="text-muted mb-4">
                      There are no RFQ items in this purchase request.
                    </p>
                  </div>
                )}
              </ModalBody>

              <ModalFooter className="bg-light">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    {hasRfqItems && (
                      <>
                        <Button
                          color="outline-warning"
                          onClick={copyAllRfqItemsToClipboard}
                          className="me-2"
                        >
                          <i className="ri-clipboard-line align-middle me-1"></i>
                          Copy All RFQ Items ({rfqItems.length})
                        </Button>
                        {/* Supplier Comparison — hidden from UI. Set to true to restore. */}
                        {false && (
                          <Button
                            color="info"
                            onClick={toggleSupplierComparisonModal}
                            className="me-2"
                          >
                            <i className="ri-bar-chart-line align-middle me-1"></i>
                            Supplier Comparison
                          </Button>
                        )}
                        <Button
                          color="success"
                          onClick={handleOpenSupplierEmailModal}
                        >
                          <i className="ri-mail-send-line align-middle me-1"></i>
                          Send Email to Suppliers
                        </Button>
                      </>
                    )}
                  </div>
                  <Button color="secondary" onClick={toggleRfqSelectionModal}>
                    Close
                  </Button>
                </div>
              </ModalFooter>
            </Modal>
            )}

            {/* Supplier Email Modal */}
            <SupplierEmailModal
              isOpen={supplierEmailModal}
              toggle={toggleSupplierEmailModal}
              selectedSupplierIds={selectedSupplierIdsForEmail}
              rfqItems={getRfqItems()}
              purchaseRequestNumber={purchaseOrder?.purchase_request_number || ""}
              purchaseRequestId={purchaseId}
            />

            {/* Attachment Modal */}
            <Modal
              isOpen={attachmentModal}
              toggle={toggleAttachmentModal}
              size="lg"
            >
              <ModalHeader toggle={toggleAttachmentModal}>
                Add New Attachment
              </ModalHeader>
              <ModalBody>
                <Form>
                  <FormGroup className="mb-3">
                    <Label for="attachmentFile">
                      File <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      id="attachmentFile"
                      onChange={handleFileChange}
                      accept="*/*"
                    />
                    <div className="form-text">
                      Select a file to upload. All file types are supported.
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label for="attachmentName">Name</Label>
                    <Input
                      type="text"
                      id="attachmentName"
                      name="name"
                      placeholder="Enter attachment name"
                      value={newAttachment.name}
                      onChange={handleAttachmentInputChange}
                    />
                    <div className="form-text">
                      Optional: Give a descriptive name for this attachment.
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label for="attachmentDescription">Description</Label>
                    <Input
                      type="textarea"
                      id="attachmentDescription"
                      name="description"
                      placeholder="Enter attachment description"
                      value={newAttachment.description}
                      onChange={handleAttachmentInputChange}
                      rows={3}
                    />
                    <div className="form-text">
                      Optional: Add a description for this attachment.
                    </div>
                  </FormGroup>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onClick={handleCreateAttachment}
                  disabled={!newAttachment.file || isCreatingAttachment}
                >
                  {isCreatingAttachment ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-line align-middle me-1"></i>
                      Upload Attachment
                    </>
                  )}
                </Button>
                <Button color="secondary" onClick={toggleAttachmentModal}>
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>

            <SupplierComparisonModal
              isOpen={supplierComparisonModal}
              toggle={toggleSupplierComparisonModal}
              rfqItems={rfqItems}
              purchaseRequestNumber={purchaseOrder?.purchase_request_number || ""}
              purchaseRequestId={purchaseId}
              selectedSupplierIds={selectedSupplierIdsForEmail}
            />

            {/* Reject Purchase Request modal */}
            <Modal isOpen={rejectModal} toggle={toggleRejectModal} centered>
              <ModalHeader toggle={toggleRejectModal}>
                <i className="ri-close-circle-line text-danger me-2"></i>
                Reject Purchase Request
              </ModalHeader>
              <ModalBody>
                <div className="mb-3">
                  <Alert color="warning" className="mb-3">
                    <i className="ri-alert-line me-2"></i>
                    This will mark the purchase request for resubmission. Please provide a reason for rejection.
                  </Alert>
                  <FormGroup>
                    <Label for="rejectReason" className="fw-semibold">
                      Reason for rejection <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      id="rejectReason"
                      rows={5}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Please provide a detailed reason for rejecting this purchase request..."
                      disabled={isUpdatingStatus}
                      className="mt-2"
                    />
                    <div className="form-text mt-1">
                      This reason will be visible to the requester.
                    </div>
                  </FormGroup>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={toggleRejectModal} disabled={isUpdatingStatus}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  onClick={handleRejectConfirm}
                  disabled={isUpdatingStatus || !rejectReason.trim()}
                >
                  {isUpdatingStatus ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="ri-close-circle-line align-middle me-1"></i>
                      Confirm Rejection
                    </>
                  )}
                </Button>
              </ModalFooter>
            </Modal>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PurchaseOrderDetail;
